import { profileSchemaOutputType } from '~/util/forms/profile';
import { includeDifferentProperties } from '~/util';
import { ApiMutationOptions } from '~/util/hooks/useApiMutation';
import { ApiKeyResponse } from '~/util/types/api';
import { BaseUser, OAuthProvider } from '~/util/types/users';
import { Badge } from '~/util/types/badges';
export type editUserMeProfileType = {
    profile: profileSchemaOutputType;
    existingProfile: profileSchemaOutputType;
};

export const editUserMeProfile = (): ApiMutationOptions<
    void,
    editUserMeProfileType
> => ({
    buildRequest: ({ profile, existingProfile }) => {
        const body = includeDifferentProperties(profile, existingProfile);
        if (Object.keys(body).length === 0)
            throw new Error('No changes have been made.');
        return {
            path: '/users/@me',
            method: 'PATCH',
            body,
            attachAuth: true,
        };
    },
    invalidateQueries: ['users', '@me'],
    toastMessages: {
        loading: 'Updating user...',
        error: 'Failed to update user: ',
        success: 'User updated (Changes may take a few minutes to reflect)',
    },
});

export const clearNotificationsMutation = (): ApiMutationOptions<
    void,
    void
> => ({
    buildRequest: () => ({
        path: `/users/@me/notifications/clear`,
        method: 'POST',
        attachAuth: true,
    }),
    invalidateQueries: ['users', '@me', 'notifications'],
    toastMessages: {
        loading: undefined,
        error: undefined,
        success: undefined,
    },
});

export const newMergeRequestMutation = (): ApiMutationOptions<
    void,
    BaseUser
> => ({
    buildRequest: ({ id }) => ({
        path: `/users/merge/requests`,
        method: 'POST',
        attachAuth: true,
        body: {
            secondary_user: id,
        },
    }),
    toastMessages: {
        loading: 'Submitting merge request...',
        error: 'Failed to submit merge request: ',
        success: ({ variables: { global_name } }) =>
            `Submitted merge request for ${global_name}`,
    },
});

export const createApiKeyMutation = (): ApiMutationOptions<
    ApiKeyResponse,
    number | undefined
> => ({
    buildRequest: (minutes) => ({
        path: `/auth/api-key`,
        method: 'POST',
        attachAuth: true,
        query: {
            lifetime_minutes: minutes,
        },
    }),
    toastMessages: {
        loading: 'Creating API key...',
        error: 'Failed to create API key: ',
        success: ({
            response: {
                data: { expires },
            },
            localeDateTimeString,
        }) =>
            `API key created and copied to clipboard. Expiration date: ${localeDateTimeString(
                new Date(expires),
            )}`,
    },
    onSuccess: async ({ data: { api_key } }) => {
        await navigator.clipboard.writeText(api_key);
    },
});

export const logoutAllMutation = (): ApiMutationOptions<void, void> => ({
    buildRequest: () => ({
        path: `/auth/logout-all`,
        method: 'POST',
        attachAuth: true,
    }),
    toastMessages: {
        loading: 'Logging out all sessions...',
        error: 'Failed to logout all sessions: ',
        success: () => 'Logged out all sessions successfully',
    },
});

export type SyncBadgesMutationOptions = {
    userid: string;
    ownProfile?: boolean;
};

export const syncBadgesMutation = (): ApiMutationOptions<
    Badge[],
    SyncBadgesMutationOptions
> => ({
    buildRequest: ({ userid, ownProfile = true }) => ({
        path: ownProfile ? '/users/@me/sync' : `/users/${userid}/badges/sync`,
        method: 'POST',
        attachAuth: true,
    }),
    toastMessages: {
        loading: 'Syncing badges...',
        error: 'Failed to sync badges: ',
        success: ({ response: { data: badges } }) =>
            `Synced ${badges?.length} badges.`,
    },
    invalidateQueries: ({ variables: { userid } }) => [
        'users',
        'profile',
        userid,
    ],
});

export const editFeaturedBadge = (): ApiMutationOptions<void, string> => ({
    buildRequest: (badgeCode) => ({
        path: '/users/@me',
        method: 'PATCH',
        body: {
            featured_badge_code: badgeCode,
        },
        attachAuth: true,
    }),
    invalidateQueries: [
        ['users', 'profile'],
        ['users', '@me'],
    ],
    toastMessages: {
        loading: 'Updating badge...',
        error: 'Failed to update badge: ',
        success: 'Badge updated',
    },
});

export type UserConnectedAccountAuthorizeLink = {
    authorize_url: string;
};

export type GetConnectedAccountAuthorizeLinkMutationOptions = {
    provider: OAuthProvider;
};

export const getConnectedAccountAuthorizeLink = (): ApiMutationOptions<
    UserConnectedAccountAuthorizeLink,
    GetConnectedAccountAuthorizeLinkMutationOptions
> => ({
    buildRequest: ({ provider }) => ({
        path: `/auth/${provider.toLowerCase()}`,
        method: 'POST',
        attachAuth: true,
        body: {
            callback: `${window.location.origin}/settings?showToast=connectedAccountAdded`,
        },
    }),
    toastMessages: {
        loading: undefined,
        error: 'Failed to connect account:',
        success: undefined,
    },
});
